import { Alert, CardContent, Dialog, DialogContent, Grid, TextField, Typography } from "@mui/material";
import { AxiosResponse } from "axios";
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcFileSelection, MprcRequestErrorReport, MprcRequestIndicator, MprcText } from "components";
import { ButtonCancel, ButtonClose, ButtonError } from "components/buttons";
import { AuthFieldsSettingsResponse, FileGetDto, StringImportedItemModel } from "generated/CoreApi_v1";
import ResourceUtils from "lib/ResourceUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface IProps {
    importType: "file" | "list";
    onClose: (success: boolean) => void;
    onImportFile: (fileId: string) => Promise<AxiosResponse<StringImportedItemModel[]>>;
    onImportList: (users: string[]) => Promise<AxiosResponse<StringImportedItemModel[]>>;
}

export const BatchEditImportUserDialog: React.FC<IProps> = (props) => {

    const { importType, onClose, onImportFile, onImportList } = props;

    const { t } = useTranslation("core");
    const api = useCoreApi();
    const session = useSession();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState<boolean>(true);
    const [importing, setImporting] = useState<boolean>(false);
    const [hasImportError, setHasImportError] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [file, setFile] = useState<FileGetDto | null>();
    const [uploading, setUploading] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const lastErrors = useRef<StringImportedItemModel[]>([]);
    const success = useRef<boolean>(false);
    const [textList, setTextList] = useState<string>();
    const [settings, setSettings] = useState<AuthFieldsSettingsResponse | undefined>(undefined);

    const inputRef = useRef<HTMLInputElement>(undefined);

    const authFields = settings?.possibleAuthFields?.map(x => ResourceUtils.findName(x.labelR, session.user.locale, session.config.defaultLocale)).join(` ${t("Literal.Or", { ns: "shared" }).toUpperCase()} `);

    const importFile = importType == "file";

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.foundationSettings.authFieldsSettings();
                setSettings(result.data);
            }
            catch (e) {
                setViewError(e as Error);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    // Ao importar
    useEffect(() => {
        async function postData() {
            try {
                lastErrors.current = [];

                if (importFile) {
                    if (file?.fileId) {
                        const result = await onImportFile(file.fileId);

                        const imported = result.data.filter(x => x.success);
                        const errors = result.data.filter(x => !x.success);

                        if (imported.length > 0) {
                            success.current = true;
                            enqueueSnackbar(t("User.SearchUsers.Import.SuccessMessage", { count: imported.length }), { variant: "success" });
                        }
                        if (errors.length > 0) {
                            lastErrors.current = errors;
                            setHasImportError(true);
                        } else {
                            onClose(true);
                        }
                    }
                } else {
                    var users = textList?.split("\n") as string[];

                    const result = await onImportList(users);

                    const imported = result.data.filter(x => x.success);
                    const errors = result.data.filter(x => !x.success);

                    if (imported.length > 0) {
                        success.current = true;
                        enqueueSnackbar(t("User.SearchUsers.Import.SuccessMessage", { count: imported.length }), { variant: "success" });
                    }
                    if (errors.length > 0) {
                        lastErrors.current = errors;
                        setHasImportError(true);
                    } else {
                        onClose(true);
                    }
                }
            }
            catch (e) {
                setViewError(e as Error);
            }
            finally {
                setImporting(false);
            }
        }

        if (importing) postData();

    }, [importing]);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const RenderErrors = (
        <MprcContainer>
            <CardContent>
                <Grid container justifyContent="center" alignItems="center" spacing={1}>
                    {
                        lastErrors.current?.map((item, index) => {
                            return (
                                <Grid size={{ xs: 12 }} key={index}>
                                    <Grid container justifyContent="start" alignItems="center" spacing={1}>
                                        <Grid>
                                            <Typography variant="h5">
                                                {item.item}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    {
                                        item.errorMessage?.map(message =>
                                            <Grid container justifyContent="start" alignItems="center" spacing={1}>
                                                <Grid>
                                                    <MprcText color={["muted", "light"]}>
                                                        <Typography variant="caption">
                                                            {message}
                                                        </Typography>
                                                    </MprcText>
                                                </Grid>
                                            </Grid>
                                        )
                                    }
                                </Grid>
                            );
                        })
                    }
                </Grid>
            </CardContent>
            <Grid container direction="column">
                <Grid size={{ xs: 12 }}>
                    <Grid container justifyContent="end" spacing={2}>
                        <Grid>
                            <ButtonClose
                                variant="contained"
                                onClick={() => onClose(success.current)}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </MprcContainer>
    );

    ///
    const RenderForm = (
        <Grid container direction="column">
            <Grid size={{ xs: 12 }}>
                <span>
                    {
                        t(importFile
                            ? "User.SearchUsers.Import.File.LoadFileSubtitle"
                            : "User.SearchUsers.Import.List.TypeOrPasteLabel")
                    }
                </span>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <span>{t("User.SearchUsers.Import.IdentificationLabel")}</span>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <span>{`${t("User.SearchUsers.Import.IdentificationOptionsText")} ${authFields}`}</span>
            </Grid>
            <Grid size={{ xs: 12 }} mt={1}>
                {
                    importFile
                        ?
                        <MprcContainer>
                            <MprcFileSelection
                                enableClear={!hasImportError}
                                acceptedFiles={{ "application/Excel": [".xlsx", ".xls"], "text/plain": [".txt"] }}
                                selectedFile={file}
                                onFileUploadStarted={
                                    () => {
                                        setUploading(true);
                                        setFile(undefined);
                                    }
                                }
                                onFileUploadError={() => {
                                    setFile(null);
                                }}
                                onFileUploadFinished={() => setUploading(false)}
                                onFileUploadSuccess={(uploadResult) => setFile(uploadResult.mainFile)}
                                onClear={() => setFile(null)}
                            />
                        </MprcContainer>
                        :
                        <TextField
                            fullWidth
                            multiline
                            autoFocus
                            rows={5}
                            variant="outlined"
                            inputRef={inputRef}
                            value={textList}
                            onChange={(el) => {
                                setHasImportError(false);
                                setTextList(el.currentTarget.value);
                            }}
                        />
                }
            </Grid>
            {
                hasImportError &&
                <Grid size={{ xs: 12 }} mt={1}>
                    <Alert
                        color="error"
                        variant="filled"
                        severity="error"
                    >
                        {t("User.SearchUsers.Import.ErrorImportMessage")}
                    </Alert>
                </Grid>
            }
            <Grid size="grow" mt={2} >
                <Grid container justifyContent="end" spacing={2}>
                    {
                        !hasImportError && !showErrors &&
                        <Grid>
                            <ButtonCancel
                                variant="contained"
                                onClick={() => onClose(success.current)}
                            />
                        </Grid>
                    }
                    {
                        lastErrors.current.length > 0 && !showErrors &&
                        <Grid>
                            <ButtonError
                                variant="contained"
                                onClick={() => setShowErrors(true)}
                            />
                        </Grid>
                    }
                    {

                        !hasImportError && !showErrors &&
                        <Grid>
                            <MprcButton
                                text={t("User.SearchUsers.Import.ImportButtonText")}
                                title={t("User.SearchUsers.Import.ImportButtonTitle")}
                                disabled={((file == undefined || file == null) && (textList == null || textList.trim().length == 0)) || uploading || importing}
                                color="info"
                                variant="contained"
                                onClick={() => {
                                    setHasImportError(false);
                                    setImporting(true);
                                }}
                            />
                        </Grid>
                    }
                </Grid>
            </Grid>
        </Grid>
    );

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <div>
            <Dialog open fullWidth maxWidth="sm" onClose={() => onClose(success.current)}>
                <MprcDialogTitleClose
                    title={
                        t(importFile ? "User.SearchUsers.Import.File.Title" : "User.SearchUsers.Import.List.Title")
                    }
                    onClose={() => onClose(success.current)}
                    shrinkPadding
                />
                <DialogContent>
                    <MprcRequestErrorReport error={viewError} />
                    <MprcRequestIndicator type={"post"} visible={loading || importing} />

                    {showErrors
                        ? RenderErrors
                        : RenderForm
                    }

                </DialogContent>
            </Dialog>
        </div>
    );
};
